import { MouseEvent, ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GestureResponderEvent } from 'react-native';
import { Alert } from '@/common/components/Alert/Alert';
import { Button } from '@/common/components/Button/Button';
import {
  CardPocket,
  TCardPocketValueProps,
} from '@/common/components/CardPocket/CardPocket';
import { Icon } from '@/common/components/Icons/Icon';
import { LogoAmericanExpress } from '@/common/components/Illustrations/CardBrands/LogoAmericanExpress';
import { LogoDinersClub } from '@/common/components/Illustrations/CardBrands/LogoDinersClub';
import { LogoDiscover } from '@/common/components/Illustrations/CardBrands/LogoDiscover';
import { LogoJCB } from '@/common/components/Illustrations/CardBrands/LogoJCB';
import { LogoMastercard } from '@/common/components/Illustrations/CardBrands/LogoMastercard';
import { LogoUnionPay } from '@/common/components/Illustrations/CardBrands/LogoUnionPay';
import { LogoVisa } from '@/common/components/Illustrations/CardBrands/LogoVisa';
import { Label } from '@/common/components/Label/Label';
import { RouterLink } from '@/common/components/RouterLink/RouterLink';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import { ITMealBuoniPastoWelcomeLearningCenter } from '@/common/containers/Dashboard/ITMealBuoniPastoWelcomeLearningCenter/ITMealBuoniPastoWelcomeLearningCenter';
import { useGetMealPaymentStrategy } from '@/common/containers/Dashboard/MealVoucher/MealVoucher.utils';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useGetMealConfiguration } from '@/common/integrations/menhir/employee/meal/meal.hooks';
import { IEmployeePaymentMethodMetaData } from '@/common/integrations/menhir/employee/payments/payments.types';
import { useGetPockets } from '@/common/integrations/menhir/employee/pockets/pockets.hooks';
import { useGetVouchersBalance } from '@/common/integrations/menhir/employee/vouchers/vouchers.hooks';
import {
  MoneyUtils,
  defaultMoney,
  removeFalsey,
} from '@coverflex-tech/hypernova-utils';
import { useRouter } from 'expo-router';

export function ITMealVoucherCardPocket() {
  const { t } = useTranslation();
  const router = useRouter();
  const { vouchersBalance, isInitialLoading: isLoadingVouchers } =
    useGetVouchersBalance('meal');
  const { mealConfiguration } = useGetMealConfiguration();
  const { creditsPocket, isInitialLoading: isLoadingCredits } = useGetPockets();
  const mealPaymentStrategy = useGetMealPaymentStrategy();

  const creditsPocketBalance = creditsPocket?.balance ?? defaultMoney;
  const vouchersBalanceAmount = vouchersBalance?.totalAmount ?? defaultMoney;

  const [showModal, setShowModal] = useState(false);
  const [setupFlow, setSetupFlow] = useState(false);
  const wasButtonClicked = useRef(false);

  const noVouchers =
    vouchersBalance && vouchersBalance.totalAmount.amount === 0;

  const cardBrand =
    mealConfiguration?.singleTransaction?.paymentMethod.metadata.brand;

  const values = [
    {
      label: noVouchers
        ? t(
            'dashboard.it.meal.buoni_pasto.widget.amount.description.no_vouchers'
          )
        : t('dashboard.it.meal.buoni_pasto.widget.amount.description', {
            count: vouchersBalance?.details[0]?.quantity,
            amount: MoneyUtils.moneyToString(
              vouchersBalance?.details[0]?.amount
            ),
          }),
      value: MoneyUtils.moneyToString(vouchersBalance?.totalAmount),
      loading: isLoadingVouchers,
    },
    mealPaymentStrategy === 'card' && {
      label: (
        <Stack horizontal gap="sm" align="center">
          {cardBrandMapping[cardBrand ?? 'unknown'] ?? (
            <Icon icon="credit-card" />
          )}
          <Label size="sm">
            {t('dashboard.it.meal.buoni_pasto.widget.card.linked.description', {
              cardNumber:
                mealConfiguration?.singleTransaction?.paymentMethod.metadata
                  .last4Digits,
            })}
          </Label>
        </Stack>
      ),
      type: 'secondary',
    },
    mealPaymentStrategy === 'credits' && {
      label:
        creditsPocketBalance.amount === 0
          ? t(
              'dashboard.it.meal.buoni_pasto.widget.credits.description.no_credits'
            )
          : t('dashboard.it.meal.buoni_pasto.widget.credits.description', {
              credits: MoneyUtils.moneyToString(creditsPocketBalance),
            }),
      value: MoneyUtils.moneyToString(creditsPocketBalance),
      type: 'secondary',
      loading: isLoadingCredits,
    },
  ].filter(removeFalsey) as TCardPocketValueProps[];

  const handleActivatePress = (
    e?: MouseEvent | GestureResponderEvent,
    isToSetup?: boolean
  ) => {
    e?.stopPropagation();

    setSetupFlow(isToSetup ?? false);

    if (!mealConfiguration?.singleTransaction && !creditsPocket) {
      setShowModal(true);
    } else {
      router.push(
        isToSetup ? ROUTES.MEAL_VOUCHER_PAYMENT : ROUTES.MEAL_VOUCHER
      );
    }
  };

  return (
    <>
      <CardPocket
        title={t('dashboard.it.meal.buoni_pasto.widget.title')}
        icon={<Icon icon="utensils" />}
        values={values}
        onPress={(e) => {
          if (!wasButtonClicked.current) {
            handleActivatePress(e, false);
          }
          wasButtonClicked.current = false;
          AnalyticsService.trackCustomEvent('home_buoni_pasto_card_clicked', {
            current_strategy: mealPaymentStrategy,
          });
        }}
      >
        {!(isLoadingVouchers || isLoadingCredits) && (
          <>
            {mealPaymentStrategy === 'none' && (
              <Button
                variant="tertiary"
                iconAlign="right"
                size="sm"
                icon={<Icon icon="arrow-right" />}
                onPress={() => {
                  wasButtonClicked.current = true;
                  handleActivatePress(undefined, true);
                  AnalyticsService.trackCustomEvent(
                    'single_transaction_home_select_payment_method_button_clicked'
                  );
                }}
                className="self-start"
              >
                {t('dashboard.it.meal.buoni_pasto.widget.setup.button.label')}
              </Button>
            )}
            {mealPaymentStrategy === 'credits' &&
              creditsPocketBalance.amount === 0 && (
                <RouterLink
                  href={ROUTES.CREDITS_ADD}
                  onPress={(evt) => evt?.stopPropagation()}
                  asChild
                >
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={<Icon icon="arrow-right" />}
                    presentational
                    iconAlign="right"
                    className="self-start"
                  >
                    {t(
                      'dashboard.it.meal.buoni_pasto.widget.credits.button.label'
                    )}
                  </Button>
                </RouterLink>
              )}
            {mealPaymentStrategy === 'credits' &&
              creditsPocketBalance.amount > 0 &&
              vouchersBalanceAmount.amount === 0 && (
                <Alert type="info" noBackground>
                  {t('dashboard.it.meal.buoni_pasto.widget.credits.alert')}
                </Alert>
              )}
          </>
        )}
      </CardPocket>
      {showModal && (
        <ITMealBuoniPastoWelcomeLearningCenter
          onClose={() => {
            setShowModal(false);
            router.navigate(
              setupFlow ? ROUTES.MEAL_VOUCHER_PAYMENT : ROUTES.MEAL_VOUCHER
            );
          }}
        />
      )}
    </>
  );
}

export const cardBrandMapping: Record<
  IEmployeePaymentMethodMetaData['brand'],
  ReactElement
> = {
  visa: <LogoVisa width={27} height={10} />,
  mastercard: <LogoMastercard width={22} height={14} />,
  amex: <LogoAmericanExpress width={24} height={24} />,
  diners: <LogoDinersClub width={24} height={24} />,
  discover: <LogoDiscover width={24} height={24} />,
  jcb: <LogoJCB width={24} height={24} />,
  unionpay: <LogoUnionPay width={24} height={24} />,
  unknown: <Icon icon="credit-card" />,
};
